import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useSalespersons } from '@/hooks/useSalespersons';
import { toggleSalesperson } from '@/services/salespersons';
import SalespersonModal from './components/SalespersonModal';

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function SalespersonsPage() {
  const { data, loading, error, refetch } = useSalespersons();
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [togglingId, setTogglingId] = useState('');

  const filtered = data.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.fullName.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) ||
      s.phone.toLowerCase().includes(q)
    );
  });

  async function handleToggle(id: string, current: boolean) {
    setTogglingId(id);
    await toggleSalesperson(id, !current);
    setTogglingId('');
    refetch();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Vendedores</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} vendedores registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nuevo Vendedor
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Vendedores</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Activos</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">{data.filter((s) => s.isActive).length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Comisión promedio</p>
            <p className="text-xl font-bold text-accent-600 mt-1">
              {data.length
                ? `${(data.reduce((s, x) => s + x.commissionRate, 0) / data.length).toFixed(1)}%`
                : '0%'}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nombre, correo o teléfono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Vendedor</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Contacto</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Comisión</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Alta</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-20 text-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando vendedores...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((s) => (
                    <tr key={s.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-accent-50 flex items-center justify-center flex-shrink-0">
                            <span className="text-accent-600 font-semibold text-sm">
                              {s.fullName.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-foreground-900 font-medium">{s.fullName}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-foreground-700">{s.email || '—'}</p>
                        <p className="text-xs text-foreground-500">{s.phone || '—'}</p>
                      </td>
                      <td className="px-5 py-3 text-right font-medium text-foreground-900 whitespace-nowrap">
                        {s.commissionRate}%
                      </td>
                      <td className="px-5 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${s.isActive ? 'text-emerald-700 bg-emerald-50' : 'text-foreground-500 bg-background-100'}`}>
                          {s.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(s.createdAt)}</td>
                      <td className="px-5 py-3 text-center">
                        <button
                          onClick={() => handleToggle(s.id, s.isActive)}
                          disabled={togglingId === s.id}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                          title={s.isActive ? 'Desactivar' : 'Activar'}
                        >
                          <i className={`${s.isActive ? 'ri-toggle-line text-emerald-600' : 'ri-toggle-fill text-foreground-400'} text-lg`}></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-team-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay vendedores registrados.'
                  : 'No se encontraron vendedores con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showNew && <SalespersonModal onClose={() => setShowNew(false)} onSaved={refetch} />}
    </AdminLayout>
  );
}